import { motion } from 'framer-motion';
import React, { FC, useCallback, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import Button from '../../shared/buttonComponents/Button';
import SearchSVG from '../../shared/svg/SearchSVG';
import { useWizard } from '../../../contexts/WizardContext';
import { Element, Link as ScrollLink, scrollSpy } from 'react-scroll';

interface IndustryListItem {
  name: string;
  risk: string;
  selected: boolean;
}

interface IndustriesPopupProps {
  industry: string;
  industriesList: IndustryListItem[];
  onClose: () => void;
  onSubmit: (selected: any) => void;
  industries?: IndustryListItem[];
}

const IndustriesPopupLetters: FC<IndustriesPopupProps> = ({ industriesList, onClose, onSubmit }): JSX.Element => {
  const {
    updateStepData,
  } = useWizard();

  scrollSpy.update();

  const [listAll, setListAll] = useState(industriesList.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  const [listSelected, setListSelected] = useState(industriesList);

  const first = 'Other';
  listAll.sort(function (x, y) {
    return x.name === first ? -1 : y.name === first ? 1 : 0;
  });

  const alphabetArray = 'abcdefghijklmnopqrstuvwxyz'.split('');

  const [selectPreview, setSelectPreview] = useState('Nothing Selected');
  const [searchSelect, setSearchSelect] = useState('');

  const handleSelect = useCallback((name, risk, selected) => {
    setListSelected((state: any[]) => {
      const res = state.map((s) => {
        if (s.name === name) {
          return {
            ...s,
            selected,
          };
        }

        return {
          ...s,
          selected: false
        };
      });

      setListAll(res);

      return res;
    });

    setSelectPreview(`${selectPreview === name ? 'Nothing Selected' : name}`);
  }, [setSelectPreview, setListSelected, setListAll, selectPreview]);

  const handleSearch = useCallback((e) => {
    const searchTerm = e.target.value;

    if (searchTerm === '') {
      setListAll(listSelected);
      setSearchSelect(searchTerm);
    } else {
      const reg = new RegExp(searchTerm, 'gi');
      setListAll(listSelected.filter((item) => !!item.name.match(reg)));
      setSearchSelect(searchTerm);
    }

  }, [listSelected]);

  const handleSubmit = useCallback(() => {
    const selected = listAll.filter(c => c.selected);

    if (selected.length === 0) {
      updateStepData({
        customIndustry: searchSelect
      });
    } else {
      updateStepData({
        customIndustry: ''
      });
    }

    onSubmit(selected[0]);
  }, [listAll, onSubmit, searchSelect, updateStepData]);

  const getElementName = (industry: IndustryListItem, prevIndustry: IndustryListItem): string => {
    let industryFirst = industry.name.charAt(0).toLowerCase();
    const prevIndustryFirst = prevIndustry.name.charAt(0).toLowerCase();
    // return industryFirst === prevIndustryFirst ? '' : industryFirst;

    if (industry.name.includes('Agriculture - Farm')) {
      return 'a'

    } else if (industryFirst === prevIndustryFirst) {
      return ''

    } else {
      return industryFirst
    }
  };

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        onClose();
      }}
    >
      <motion.div
        initial={{
          opacity: 0,
          scale: 0
        }}
        animate={{
          opacity: 1,
          scale: 1
        }}
        exit={{
          opacity: 0,
          scale: 0
        }}
        transition={{
          duration: 0.5
        }}
        className="industries-popup-other-container"
      >

        <div className="continents-popup">
          <div className="continents-popup-close">
            <img
              onClick={onClose}
              src={`${process.env.PUBLIC_URL}/assets/images/close-icon.png`}
              alt="Close"
            />
          </div>

          <div className="continents-popup-title">
            Please type or choose your sub-industry.
          </div>

          <div className="continents-popup-search-box">
            <div className="continents-popup-search">
              <input
                onChange={handleSearch}
                placeholder="You can search here..."
                type="text"
              />
              <div className="search-icon">
                <SearchSVG/>
              </div>
            </div>
          </div>

          <div className='selected-items-box'>
            {selectPreview}
          </div>

          <div className='letters-list'>
            {alphabetArray.map((letter, index) => (
              <div key={letter + index} className='letter-box'>
                <ScrollLink
                  activeClass='letter-active'
                  to={letter}
                  spy={true}
                  smooth={true}
                  duration={300}
                  className='letter-link'
                  containerId='industries-scroll'
                >
                <span
                  key={letter}
                  className='letter'
                >
                  {letter.toUpperCase()}
                </span>
                </ScrollLink>
              </div>
            ))}
          </div>

          <Element name='industries-scroll' id='industries-scroll' className="continents-popup-list">
            {listAll.length === 0
              ?
              <p>Our algorithm didn't recognize the sub-industry you selected so we are unable to generate an accurate
                price proposal for you at this time. Please choose another industry or click Next. One of our team members will get back to you shortly.</p>
              :
              listAll
                .map((industry, index, arr) => (
                  <Element
                    key={`${index}-${industry.name}`}
                    name={getElementName(industry, arr[index === 0 ? index : index - 1])}
                    id={industry.name}
                    className='proposal-box'
                  >
                    <div className="continent-box">
                      <div className="continent-checkbox-box">
                        <input
                          onChange={() => handleSelect(
                            industry.name,
                            industry.risk,
                            !industry.selected
                          )}
                          id={`checkbox-item-${index}`}
                          className="continent-checkbox"
                          type="checkbox"
                          checked={industry.selected}
                        />
                        <label htmlFor={`checkbox-item-${index}`}/>
                      </div>

                      <div
                        onClick={() => handleSelect(
                          industry.name,
                          industry.risk,
                          !industry.selected
                        )}
                        className="continent-title"
                      >
                        {industry.name}
                      </div>
                    </div>
                  </Element>
                ))}
          </Element>

          <div className="continents-popup-button">
            <Button
              title="Next"
              onClick={handleSubmit}
              disabled={!(selectPreview !== 'Nothing Selected' || searchSelect !== '')}
            />
          </div>
        </div>

        <div className="industries-popup-mobile">
          <div className="continents-popup-close">
            <img
              onClick={onClose}
              src={`${process.env.PUBLIC_URL}/assets/images/X2x.png`}
              alt=""
            />
          </div>

          <div className="continents-popup-title">
            Please type or choose your sub-industry.
          </div>

          <div className="continents-popup-search-box">
            <div className="continents-popup-search">
              <input
                onChange={handleSearch}
                placeholder="You can search here..."
                type="text"
              />
              <div className="search-icon">
                <SearchSVG/>
              </div>
            </div>
          </div>

          <div className='selected-items-box-mobile'>
            {selectPreview}
          </div>

          <div className='letters-list'>
            {alphabetArray.map((letter, index) => (
              <div key={letter + index} className='letter-box'>
                <ScrollLink
                  activeClass='letter-active'
                  to={`${letter}-mobile`}
                  spy={true}
                  smooth={true}
                  duration={100}
                  className='letter-link'
                  containerId='industries-scroll-mobile'
                >
              <span
                key={letter + index}
                className='letter'
              >
                {letter.toUpperCase()}
              </span>
                </ScrollLink>
              </div>
            ))}
          </div>

          <Element name='industries-scroll-mobile' id='industries-scroll-mobile'
                   className="continents-popup-list scroll-popup-mobile">
            {listAll.length === 0
              ?
              <p>'Our algorithm didn't recognize the sub-industry you selected so we are unable to generate an accurate
                price proposal for you at this time. Please choose another industry or click Next. One of our team members will get back to you shortly.</p>
              :
              listAll
                .map((industry, index, arr) => (
                  <Element
                    key={`${index}-mobile-${industry.name}`}
                    name={`${getElementName(industry, arr[index === 0 ? index : index - 1])}-mobile`}
                    id={industry.name}
                    className="continent-box">
                    <div className="continent-checkbox-box">
                      <input
                        onChange={() => handleSelect(
                          industry.name,
                          industry.risk,
                          !industry.selected
                        )}
                        id={`checkbox-item-${index}`}
                        className="continent-checkbox"
                        type="checkbox"
                        checked={industry.selected}
                      />
                      <label htmlFor={`checkbox-item-${index}`}/>
                    </div>

                    <div
                      onClick={() => handleSelect(
                        industry.name,
                        industry.risk,
                        !industry.selected
                      )}
                      className="continent-title"
                    >
                      {industry.name}
                    </div>
                  </Element>
                ))}
          </Element>

          <div className="continents-popup-button">
            <Button
              title="Next"
              onClick={handleSubmit}
              disabled={!(selectPreview !== 'Nothing Selected' || searchSelect !== '')}
            />
          </div>
        </div>

      </motion.div>
    </OutsideClickHandler>
  );
};

export default IndustriesPopupLetters;
